import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  CreditCard,
  FileWarning,
  Plane,
  Send,
  History,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Mail,
  Clock,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { NotificationPreviewModal } from '@/components/travel/notifications/preview-modal';
import { BatchSendDialog } from '@/components/travel/notifications/batch-send-dialog';

type TabType = 'payments' | 'documents' | 'departures' | 'history';

export function NotificationsPage() {
  const { t, i18n } = useTranslation('notifications');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const [activeTab, setActiveTab] = useState<TabType>('payments');
  const [previewModal, setPreviewModal] = useState<{
    open: boolean;
    type: 'payment' | 'document' | 'departure';
    reminderId: string;
  } | null>(null);
  const [batchDialog, setBatchDialog] = useState<{
    open: boolean;
    type: 'payment' | 'document' | 'departure';
  } | null>(null);

  const { data: allReminders, isLoading, refetch } = trpc.notifications.getAllPendingReminders.useQuery();
  const { data: history } = trpc.notifications.getNotificationHistory.useQuery(
    {},
    { enabled: activeTab === 'history' }
  );

  const sendMutation = trpc.notifications.sendNotification.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const tabs = [
    { id: 'payments' as const, icon: CreditCard, count: allReminders?.totals.payments || 0 },
    { id: 'documents' as const, icon: FileWarning, count: allReminders?.totals.documents || 0 },
    { id: 'departures' as const, icon: Plane, count: allReminders?.totals.departures || 0 },
    { id: 'history' as const, icon: History, count: null },
  ];

  const handleSendSingle = async (type: 'payment' | 'document' | 'departure', reminderId: string) => {
    await sendMutation.mutateAsync({ type, reminderId });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">{t('title')}</h1>
        <p className="text-muted-foreground">{t('description')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { key: 'total', value: allReminders?.totals.total || 0, icon: Bell, color: 'text-violet-600', bg: 'bg-violet-100' },
          { key: 'payments', value: allReminders?.totals.payments || 0, icon: CreditCard, color: 'text-orange-600', bg: 'bg-orange-100' },
          { key: 'documents', value: allReminders?.totals.documents || 0, icon: FileWarning, color: 'text-amber-600', bg: 'bg-amber-100' },
          { key: 'departures', value: allReminders?.totals.departures || 0, icon: Plane, color: 'text-blue-600', bg: 'bg-blue-100' },
        ].map((stat) => (
          <div key={stat.key} className="border border-border bg-card p-4">
            <div className="flex items-center gap-3">
              <div className={`${stat.bg} p-2`}>
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{t(`stats.${stat.key}`)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-foreground text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {t(`tabs.${tab.id}`)}
              {tab.count !== null && tab.count > 0 && (
                <span className="bg-primary text-primary-foreground px-2 py-0.5 text-xs font-medium">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="border border-border bg-card overflow-hidden">
        {activeTab === 'payments' && (
          <PaymentRemindersTable
            reminders={allReminders?.payments || []}
            onPreview={(id) => setPreviewModal({ open: true, type: 'payment', reminderId: id })}
            onSend={(id) => handleSendSingle('payment', id)}
            onSendAll={() => setBatchDialog({ open: true, type: 'payment' })}
            sending={sendMutation.isPending}
            dateLocale={dateLocale}
          />
        )}

        {activeTab === 'documents' && (
          <DocumentAlertsTable
            reminders={allReminders?.documents || []}
            onPreview={(id) => setPreviewModal({ open: true, type: 'document', reminderId: id })}
            onSend={(id) => handleSendSingle('document', id)}
            onSendAll={() => setBatchDialog({ open: true, type: 'document' })}
            sending={sendMutation.isPending}
            dateLocale={dateLocale}
          />
        )}

        {activeTab === 'departures' && (
          <DepartureRemindersTable
            reminders={allReminders?.departures || []}
            onPreview={(id) => setPreviewModal({ open: true, type: 'departure', reminderId: id })}
            onSend={(id) => handleSendSingle('departure', id)}
            onSendAll={() => setBatchDialog({ open: true, type: 'departure' })}
            sending={sendMutation.isPending}
            dateLocale={dateLocale}
          />
        )}

        {activeTab === 'history' && (
          <NotificationHistoryTable communications={history?.communications || []} dateLocale={dateLocale} />
        )}
      </div>

      {/* Preview Modal */}
      {previewModal && (
        <NotificationPreviewModal
          open={previewModal.open}
          onClose={() => setPreviewModal(null)}
          type={previewModal.type}
          reminderId={previewModal.reminderId}
          onSend={() => {
            handleSendSingle(previewModal.type, previewModal.reminderId);
            setPreviewModal(null);
          }}
          sending={sendMutation.isPending}
        />
      )}

      {/* Batch Send Dialog */}
      {batchDialog && (
        <BatchSendDialog
          open={batchDialog.open}
          onClose={() => setBatchDialog(null)}
          type={batchDialog.type}
          count={
            batchDialog.type === 'payment'
              ? allReminders?.totals.payments || 0
              : batchDialog.type === 'document'
              ? allReminders?.totals.documents || 0
              : allReminders?.totals.departures || 0
          }
          onSuccess={() => {
            setBatchDialog(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

// Payment Reminders Table Component
function PaymentRemindersTable({
  reminders,
  onPreview,
  onSend,
  onSendAll,
  sending,
  dateLocale,
}: {
  reminders: any[];
  onPreview: (id: string) => void;
  onSend: (id: string) => void;
  onSendAll: () => void;
  sending: boolean;
  dateLocale: string;
}) {
  const { t } = useTranslation('notifications');

  if (reminders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-4" />
        <p className="font-medium">{t('noReminders')}</p>
        <p className="text-sm text-muted-foreground">{t('allCaughtUp')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <h3 className="font-semibold">{t('paymentReminders')}</h3>
        <Button onClick={onSendAll} size="sm" className="h-8 gap-2">
          <Send className="h-4 w-4" />
          {t('sendAll')} ({reminders.length})
        </Button>
      </div>
      <div className="divide-y divide-border">
        {reminders.map((reminder) => (
          <div key={reminder.id} className="flex items-center justify-between px-6 py-4 hover:bg-muted/30">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium">{reminder.customerName}</span>
                {reminder.details.isOverdue && (
                  <span className="bg-red-100 text-red-700 px-2 py-0.5 text-xs font-medium flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {t('overdue')}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground truncate">
                {reminder.details.bookingReference} - {reminder.details.packageName}
              </p>
            </div>
            <div className="text-center px-4">
              <p className="font-semibold">{formatCurrency(reminder.details.amount - reminder.details.paidAmount)}</p>
              <p className="text-xs text-muted-foreground">{t('amount')}</p>
            </div>
            <div className="text-center px-4">
              <p className="font-medium">{new Date(reminder.dueDate).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })}</p>
              <p className="text-xs text-muted-foreground">{reminder.daysUntil} {t('daysUntil')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-8" onClick={() => onPreview(reminder.id)}>
                {t('preview')}
              </Button>
              <Button size="sm" className="h-8" onClick={() => onSend(reminder.id)} disabled={sending}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// Document Alerts Table Component
function DocumentAlertsTable({
  reminders,
  onPreview,
  onSend,
  onSendAll,
  sending,
  dateLocale,
}: {
  reminders: any[];
  onPreview: (id: string) => void;
  onSend: (id: string) => void;
  onSendAll: () => void;
  sending: boolean;
  dateLocale: string;
}) {
  const { t } = useTranslation('notifications');

  if (reminders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-4" />
        <p className="font-medium">{t('noReminders')}</p>
        <p className="text-sm text-muted-foreground">{t('allCaughtUp')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <h3 className="font-semibold">{t('documentAlerts')}</h3>
        <Button onClick={onSendAll} size="sm" className="h-8 gap-2">
          <Send className="h-4 w-4" />
          {t('sendAll')} ({reminders.length})
        </Button>
      </div>
      <div className="divide-y divide-border">
        {reminders.map((reminder) => (
          <div key={reminder.id} className="flex items-center justify-between px-6 py-4 hover:bg-muted/30">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium">{reminder.customerName}</span>
                {reminder.daysUntil <= 7 && (
                  <span className="bg-red-100 text-red-700 px-2 py-0.5 text-xs font-medium flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {t('urgent')}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground truncate">
                {reminder.details.documentType}: {reminder.details.documentNumber || reminder.details.passportNumber || 'N/A'}
              </p>
            </div>
            <div className="text-center px-4">
              <p className="font-medium">{new Date(reminder.dueDate).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })}</p>
              <p className="text-xs text-muted-foreground">{t('expiryDate')}</p>
            </div>
            <div className="text-center px-4">
              <p className={`font-semibold ${reminder.daysUntil <= 7 ? 'text-red-600' : ''}`}>
                {reminder.daysUntil}
              </p>
              <p className="text-xs text-muted-foreground">{t('daysUntil')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-8" onClick={() => onPreview(reminder.id)}>
                {t('preview')}
              </Button>
              <Button size="sm" className="h-8" onClick={() => onSend(reminder.id)} disabled={sending}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// Departure Reminders Table Component
function DepartureRemindersTable({
  reminders,
  onPreview,
  onSend,
  onSendAll,
  sending,
  dateLocale,
}: {
  reminders: any[];
  onPreview: (id: string) => void;
  onSend: (id: string) => void;
  onSendAll: () => void;
  sending: boolean;
  dateLocale: string;
}) {
  const { t } = useTranslation('notifications');

  if (reminders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-4" />
        <p className="font-medium">{t('noReminders')}</p>
        <p className="text-sm text-muted-foreground">{t('allCaughtUp')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <h3 className="font-semibold">{t('departureReminders')}</h3>
        <Button onClick={onSendAll} size="sm" className="h-8 gap-2">
          <Send className="h-4 w-4" />
          {t('sendAll')} ({reminders.length})
        </Button>
      </div>
      <div className="divide-y divide-border">
        {reminders.map((reminder) => (
          <div key={reminder.id} className="flex items-center justify-between px-6 py-4 hover:bg-muted/30">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium">{reminder.customerName}</span>
              </div>
              <p className="text-sm text-muted-foreground truncate">
                {reminder.details.bookingReference} - {reminder.details.packageName}
              </p>
            </div>
            <div className="text-center px-4">
              <p className="font-medium">{new Date(reminder.dueDate).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })}</p>
              <p className="text-xs text-muted-foreground">{t('travelDate')}</p>
            </div>
            <div className="text-center px-4">
              <p className="font-semibold">{reminder.details.passengerCount}</p>
              <p className="text-xs text-muted-foreground">{t('passengers')}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-8" onClick={() => onPreview(reminder.id)}>
                {t('preview')}
              </Button>
              <Button size="sm" className="h-8" onClick={() => onSend(reminder.id)} disabled={sending}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// Notification History Table
function NotificationHistoryTable({
  communications,
  dateLocale,
}: {
  communications: any[];
  dateLocale: string;
}) {
  const { t } = useTranslation('notifications');

  if (communications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <History className="h-12 w-12 text-muted-foreground mb-4" />
        <p className="font-medium">{t('noReminders')}</p>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    SENT: 'bg-emerald-100 text-emerald-700',
    DELIVERED: 'bg-blue-100 text-blue-700',
    FAILED: 'bg-red-100 text-red-700',
    DRAFT: 'bg-gray-100 text-gray-700',
  };

  return (
    <>
      <div className="border-b border-border px-6 py-4">
        <h3 className="font-semibold">{t('notificationHistory')}</h3>
        <p className="text-sm text-muted-foreground">{t('recentlySent')}</p>
      </div>
      <div className="divide-y divide-border">
        {communications.map((comm) => (
          <div key={comm.id} className="flex items-center justify-between px-6 py-4 hover:bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="bg-muted p-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <p className="font-medium truncate">{comm.subject || t('noSubject')}</p>
                <p className="text-sm text-muted-foreground truncate">
                  {comm.customer?.name || t('unknownCustomer')} - {comm.customer?.email}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className={`px-2 py-0.5 text-xs font-medium ${statusColors[comm.status] || 'bg-gray-100'}`}>
                {comm.status}
              </span>
              <div className="text-right">
                <p className="text-sm flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {comm.sentAt
                    ? new Date(comm.sentAt).toLocaleDateString(dateLocale, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : t('notSent')}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
